import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Webcam from 'react-webcam';
import './App.css';

function App() {
  const [step, setStep] = useState('welcome');
  const [capturedImage, setCapturedImage] = useState(null);
  const [gender, setGender] = useState('Women');
  const [referenceObject, setReferenceObject] = useState(null);
  const [tapPoints, setTapPoints] = useState([]);
  const [currentTapStep, setCurrentTapStep] = useState(0);
  const [measurements, setMeasurements] = useState({ footLength: null, footWidth: null });
  const [recommendedSize, setRecommendedSize] = useState(null);
  const [recommendedWidth, setRecommendedWidth] = useState(null);
  const [activePoint, setActivePoint] = useState(null);
  const [isStaffMode, setIsStaffMode] = useState(false);
  const [staffLoggedIn, setStaffLoggedIn] = useState(false);
  const [staffPassword, setStaffPassword] = useState("");
  const [feedbackStep, setFeedbackStep] = useState(null);
  const [sizeAccurate, setSizeAccurate] = useState(null);
  const [actualFitSize, setActualFitSize] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackData, setFeedbackData] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [showFeedbackList, setShowFeedbackList] = useState(false);
  const [pointConfirmed, setPointConfirmed] = useState(false);
  const webcamRef = useRef(null);
  const imageRef = useRef(null);

  // Check if staff mode on mount
  useEffect(() => {
    if (window.location.pathname === "/staff") {
      setIsStaffMode(true);
      const loggedIn = sessionStorage.getItem("staffLoggedIn");
      if (loggedIn === "true") {
        setStaffLoggedIn(true);
      }
    }
  }, []);

  const referenceObjects = {
    paper: { name: 'Letter Paper (8.5x11")', length: 27.94, taps: 5, footOn: true },
    dollar: { name: 'US Dollar Bill', length: 15.6, taps: 6, footOn: false },
    quarter: { name: 'US Quarter', length: 2.43, taps: 6, footOn: false },
    passport: { name: 'Passport', length: 12.5, taps: 6, footOn: false },
    card: { name: 'Credit/Debit Card', length: 8.56, taps: 6, footOn: false }
  };

  const getTapCount = () => referenceObject ? referenceObjects[referenceObject].taps : 6;

  const getInstructions = () => {
    if (!referenceObject) return [];
    const ref = referenceObjects[referenceObject];
    if (ref.footOn) {
      return [
        'Tap the TOP EDGE of the paper',
        'Tap the BOTTOM EDGE of the paper (where heel is)',
        'Tap your LONGEST TOE',
        'Tap LEFT side of the WIDEST part (ball of foot)',
        'Tap RIGHT side of the WIDEST part'
      ];
    } else {
      return [
        `Tap LEFT edge of the ${ref.name}`,
        `Tap RIGHT edge of the ${ref.name}`,
        'Tap your HEEL (back of foot)',
        'Tap your LONGEST TOE',
        'Tap LEFT side of the WIDEST part (ball of foot)',
        'Tap RIGHT side of the WIDEST part'
      ];
    }
  };

  const womensSizing = {
    narrow: [
      { size: '4N', us: 4, bottomLength: 20.416, ballGirth: 18.96 },
      { size: '4.5N', us: 4.5, bottomLength: 20.839, ballGirth: 19.2775 },
      { size: '5N', us: 5, bottomLength: 21.262, ballGirth: 19.595 },
      { size: '5.5N', us: 5.5, bottomLength: 21.685, ballGirth: 19.9125 },
      { size: '6N', us: 6, bottomLength: 22.108, ballGirth: 20.23 },
      { size: '6.5N', us: 6.5, bottomLength: 22.531, ballGirth: 20.5475 },
      { size: '7N', us: 7, bottomLength: 22.954, ballGirth: 20.865 },
      { size: '7.5N', us: 7.5, bottomLength: 23.337, ballGirth: 21.1825 },
      { size: '8N', us: 8, bottomLength: 23.8, ballGirth: 21.5 },
      { size: '8.5N', us: 8.5, bottomLength: 24.223, ballGirth: 21.8175 },
      { size: '9N', us: 9, bottomLength: 24.646, ballGirth: 22.135 },
      { size: '9.5N', us: 9.5, bottomLength: 25.069, ballGirth: 22.4525 },
      { size: '10N', us: 10, bottomLength: 25.492, ballGirth: 22.77 },
      { size: '10.5N', us: 10.5, bottomLength: 25.915, ballGirth: 23.0875 },
      { size: '11N', us: 11, bottomLength: 26.338, ballGirth: 23.405 }
    ],
    mid: [
      { size: '4M', us: 4, bottomLength: 20.416, ballGirth: 19.56 },
      { size: '4.5M', us: 4.5, bottomLength: 20.839, ballGirth: 19.8775 },
      { size: '5M', us: 5, bottomLength: 21.262, ballGirth: 20.195 },
      { size: '5.5M', us: 5.5, bottomLength: 21.685, ballGirth: 20.5125 },
      { size: '6M', us: 6, bottomLength: 22.108, ballGirth: 20.83 },
      { size: '6.5M', us: 6.5, bottomLength: 22.531, ballGirth: 21.1475 },
      { size: '7M', us: 7, bottomLength: 22.954, ballGirth: 21.465 },
      { size: '7.5M', us: 7.5, bottomLength: 23.337, ballGirth: 21.7825 },
      { size: '8M', us: 8, bottomLength: 23.8, ballGirth: 22.1 },
      { size: '8.5M', us: 8.5, bottomLength: 24.223, ballGirth: 22.4175 },
      { size: '9M', us: 9, bottomLength: 24.646, ballGirth: 22.735 },
      { size: '9.5M', us: 9.5, bottomLength: 25.069, ballGirth: 23.0525 },
      { size: '10M', us: 10, bottomLength: 25.492, ballGirth: 23.37 },
      { size: '10.5M', us: 10.5, bottomLength: 25.915, ballGirth: 23.6875 },
      { size: '11M', us: 11, bottomLength: 26.338, ballGirth: 24.005 }
    ],
    wide: [
      { size: '4W', us: 4, bottomLength: 20.416, ballGirth: 20.16 },
      { size: '4.5W', us: 4.5, bottomLength: 20.839, ballGirth: 20.4775 },
      { size: '5W', us: 5, bottomLength: 21.262, ballGirth: 20.795 },
      { size: '5.5W', us: 5.5, bottomLength: 21.685, ballGirth: 21.1125 },
      { size: '6W', us: 6, bottomLength: 22.108, ballGirth: 21.43 },
      { size: '6.5W', us: 6.5, bottomLength: 22.531, ballGirth: 21.7475 },
      { size: '7W', us: 7, bottomLength: 22.954, ballGirth: 22.065 },
      { size: '7.5W', us: 7.5, bottomLength: 23.337, ballGirth: 22.3825 },
      { size: '8W', us: 8, bottomLength: 23.8, ballGirth: 22.7 },
      { size: '8.5W', us: 8.5, bottomLength: 24.223, ballGirth: 23.0175 },
      { size: '9W', us: 9, bottomLength: 24.646, ballGirth: 23.335 },
      { size: '9.5W', us: 9.5, bottomLength: 25.069, ballGirth: 23.6525 },
      { size: '10W', us: 10, bottomLength: 25.492, ballGirth: 23.97 },
      { size: '10.5W', us: 10.5, bottomLength: 25.915, ballGirth: 24.2875 },
      { size: '11W', us: 11, bottomLength: 26.338, ballGirth: 24.605 }
    ]
  };

  const mensSizing = {
    narrow: [
      { size: '5N', us: 5, bottomLength: 21.97, ballGirth: 20.63 },
      { size: '5.5N', us: 5.5, bottomLength: 22.39, ballGirth: 20.94 },
      { size: '6N', us: 6, bottomLength: 22.82, ballGirth: 21.26 },
      { size: '6.5N', us: 6.5, bottomLength: 23.24, ballGirth: 21.58 },
      { size: '7N', us: 7, bottomLength: 23.66, ballGirth: 21.9 },
      { size: '7.5N', us: 7.5, bottomLength: 24.09, ballGirth: 22.21 },
      { size: '8N', us: 8, bottomLength: 24.51, ballGirth: 22.53 },
      { size: '8.5N', us: 8.5, bottomLength: 24.93, ballGirth: 22.85 },
      { size: '9N', us: 9, bottomLength: 25.35, ballGirth: 23.17 },
      { size: '9.5N', us: 9.5, bottomLength: 25.78, ballGirth: 23.48 },
      { size: '10N', us: 10, bottomLength: 26.2, ballGirth: 23.8 },
      { size: '10.5N', us: 10.5, bottomLength: 26.62, ballGirth: 24.12 },
      { size: '11N', us: 11, bottomLength: 27.05, ballGirth: 24.44 },
      { size: '11.5N', us: 11.5, bottomLength: 27.47, ballGirth: 24.75 },
      { size: '12N', us: 12, bottomLength: 27.89, ballGirth: 25.07 },
      { size: '12.5N', us: 12.5, bottomLength: 28.31, ballGirth: 25.39 },
      { size: '13N', us: 13, bottomLength: 28.74, ballGirth: 25.71 },
      { size: '13.5N', us: 13.5, bottomLength: 29.16, ballGirth: 26.02 },
      { size: '14N', us: 14, bottomLength: 29.58, ballGirth: 26.34 }
    ],
    mid: [
      { size: '5M', us: 5, bottomLength: 21.97, ballGirth: 21.225 },
      { size: '5.5M', us: 5.5, bottomLength: 22.39, ballGirth: 21.5425 },
      { size: '6M', us: 6, bottomLength: 22.82, ballGirth: 21.86 },
      { size: '6.5M', us: 6.5, bottomLength: 23.24, ballGirth: 22.1775 },
      { size: '7M', us: 7, bottomLength: 23.66, ballGirth: 22.495 },
      { size: '7.5M', us: 7.5, bottomLength: 24.09, ballGirth: 22.8125 },
      { size: '8M', us: 8, bottomLength: 24.51, ballGirth: 23.13 },
      { size: '8.5M', us: 8.5, bottomLength: 24.93, ballGirth: 23.4475 },
      { size: '9M', us: 9, bottomLength: 25.35, ballGirth: 23.765 },
      { size: '9.5M', us: 9.5, bottomLength: 25.78, ballGirth: 24.0825 },
      { size: '10M', us: 10, bottomLength: 26.2, ballGirth: 24.4 },
      { size: '10.5M', us: 10.5, bottomLength: 26.62, ballGirth: 24.7175 },
      { size: '11M', us: 11, bottomLength: 27.05, ballGirth: 25.035 },
      { size: '11.5M', us: 11.5, bottomLength: 27.47, ballGirth: 25.3525 },
      { size: '12M', us: 12, bottomLength: 27.89, ballGirth: 25.67 },
      { size: '12.5M', us: 12.5, bottomLength: 28.31, ballGirth: 25.9875 },
      { size: '13M', us: 13, bottomLength: 28.74, ballGirth: 26.305 },
      { size: '13.5M', us: 13.5, bottomLength: 29.16, ballGirth: 26.6225 },
      { size: '14M', us: 14, bottomLength: 29.58, ballGirth: 26.94 }
    ],
    wide: [
      { size: '5W', us: 5, bottomLength: 21.97, ballGirth: 21.875 },
      { size: '5.5W', us: 5.5, bottomLength: 22.39, ballGirth: 22.1425 },
      { size: '6W', us: 6, bottomLength: 22.82, ballGirth: 22.46 },
      { size: '6.5W', us: 6.5, bottomLength: 23.24, ballGirth: 22.7775 },
      { size: '7W', us: 7, bottomLength: 23.66, ballGirth: 23.095 },
      { size: '7.5W', us: 7.5, bottomLength: 24.09, ballGirth: 23.4125 },
      { size: '8W', us: 8, bottomLength: 24.51, ballGirth: 23.73 },
      { size: '8.5W', us: 8.5, bottomLength: 24.93, ballGirth: 24.0475 },
      { size: '9W', us: 9, bottomLength: 25.35, ballGirth: 24.365 },
      { size: '9.5W', us: 9.5, bottomLength: 25.78, ballGirth: 24.6825 },
      { size: '10W', us: 10, bottomLength: 26.2, ballGirth: 25 },
      { size: '10.5W', us: 10.5, bottomLength: 26.62, ballGirth: 25.3175 },
      { size: '11W', us: 11, bottomLength: 27.05, ballGirth: 25.635 },
      { size: '11.5W', us: 11.5, bottomLength: 27.47, ballGirth: 25.9525 },
      { size: '12W', us: 12, bottomLength: 27.89, ballGirth: 26.27 },
      { size: '12.5W', us: 12.5, bottomLength: 28.31, ballGirth: 26.5875 },
      { size: '13W', us: 13, bottomLength: 28.74, ballGirth: 26.905 },
      { size: '13.5W', us: 13.5, bottomLength: 29.16, ballGirth: 27.225 },
      { size: '14W', us: 14, bottomLength: 29.58, ballGirth: 27.54 }
    ]
  };

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    setStep('measure');
    setCurrentTapStep(0);
    setTapPoints([]);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result);
        setStep('measure');
        setCurrentTapStep(0);
        setTapPoints([]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageTap = (e) => {
    e.preventDefault();
    if (pointConfirmed) return; // Wait for confirm before next point
    const tapCount = getTapCount();
    if (currentTapStep >= tapCount) return;
    const rect = imageRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    setActivePoint({ x, y });
  };

  const handleDrag = (e) => {
    if (!activePoint) return;
    e.preventDefault();
    const rect = imageRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height));
    setActivePoint({ x, y });
  };

  const confirmPoint = () => {
    if (!activePoint) return;
    const tapCount = getTapCount();
    const newPoints = [...tapPoints, activePoint];
    setTapPoints(newPoints);
    setCurrentTapStep(currentTapStep + 1);
    setActivePoint(null);
    if (newPoints.length === tapCount) {
      calculateMeasurements(newPoints);
    }
  };

  const calculateMeasurements = (points) => {
    const ref = referenceObjects[referenceObject];
    let footLengthCm, footWidthCm, pixelsPerCm;

    if (ref.footOn) {
      // Paper mode: 5 taps - top of paper, bottom of paper, toe, left ball, right ball
      const [paperTop, paperBottom, toe, ballLeft, ballRight] = points;
      
      // Calculate pixels per cm from paper height
      const paperPixelHeight = paperBottom.y - paperTop.y;
      pixelsPerCm = paperPixelHeight / ref.length;
      
      // Foot length: from heel (paper bottom) to toe
      // In screen coordinates, Y increases downward, so heel is at higher Y than toe
      const footLengthPixels = paperBottom.y - toe.y;
      footLengthCm = footLengthPixels / pixelsPerCm;
      
      // Foot width
      const footWidthPixels = Math.sqrt(Math.pow(ballRight.x - ballLeft.x, 2) + Math.pow(ballRight.y - ballLeft.y, 2));
      footWidthCm = footWidthPixels / pixelsPerCm;
    } else {
      // Other reference objects: 6 taps - ref left, ref right, heel, toe, ball left, ball right
      const [ref1, ref2, heel, toe, ballLeft, ballRight] = points;
      const refPixelDistance = Math.sqrt(Math.pow(ref2.x - ref1.x, 2) + Math.pow(ref2.y - ref1.y, 2));
      pixelsPerCm = refPixelDistance / ref.length;
      
      const footLengthPixels = Math.sqrt(Math.pow(toe.x - heel.x, 2) + Math.pow(toe.y - heel.y, 2));
      footLengthCm = footLengthPixels / pixelsPerCm;
      
      const footWidthPixels = Math.sqrt(Math.pow(ballRight.x - ballLeft.x, 2) + Math.pow(ballRight.y - ballLeft.y, 2));
      footWidthCm = footWidthPixels / pixelsPerCm;
    }

    setMeasurements({ footLength: footLengthCm.toFixed(2), footWidth: footWidthCm.toFixed(2) });
    calculateRecommendedSize(footLengthCm, footWidthCm);
  };

  const calculateRecommendedSize = (footLength, footWidth) => {
    const sizingData = gender === 'Women' ? womensSizing : mensSizing;
    let bestMatch = null;
    let minLengthDiff = Infinity;
    sizingData.mid.forEach(shoe => {
      const diff = Math.abs(shoe.bottomLength - footLength);
      if (diff < minLengthDiff) {
        minLengthDiff = diff;
        bestMatch = shoe;
      }
    });
    if (!bestMatch) {
      alert('Could not find a matching size. Please contact ORZA support.');
      return;
    }
    const estimatedBallGirth = footWidth * 2.5;
    const narrowShoe = sizingData.narrow.find(s => s.us === bestMatch.us);
    const midShoe = sizingData.mid.find(s => s.us === bestMatch.us);
    const wideShoe = sizingData.wide.find(s => s.us === bestMatch.us);
    let recommendedWidthType = 'mid';
    let finalShoe = midShoe;
    if (narrowShoe && midShoe && wideShoe) {
      const narrowMidThreshold = (narrowShoe.ballGirth + midShoe.ballGirth) / 2;
      const midWideThreshold = (midShoe.ballGirth + wideShoe.ballGirth) / 2;
      if (estimatedBallGirth < narrowMidThreshold) {
        recommendedWidthType = 'narrow';
        finalShoe = narrowShoe;
      } else if (estimatedBallGirth > midWideThreshold) {
        recommendedWidthType = 'wide';
        finalShoe = wideShoe;
      }
    }
    setRecommendedSize(finalShoe.size);
    setRecommendedWidth(recommendedWidthType);
    setStep('result');
  };

  const getCurrentInstruction = () => {
    const instructions = getInstructions();
    return instructions[currentTapStep] || 'Calculating...';
  };

  const undoLastTap = () => {
    if (tapPoints.length > 0) {
      setTapPoints(tapPoints.slice(0, -1));
      setCurrentTapStep(currentTapStep - 1);
    }
  };

  const resetMeasurement = () => {
    setTapPoints([]);
    setCurrentTapStep(0);
    setMeasurements({ footLength: null, footWidth: null });
  };

  const returnToShopify = () => {
    window.location.href = `https://orzabrand.com/collections/ballet-shoes?size=${recommendedSize}`;
  };

  const reset = () => {
    setStep('welcome');
    setCapturedImage(null);
    setReferenceObject(null);
    setTapPoints([]);
    setCurrentTapStep(0);
    setMeasurements({ footLength: null, footWidth: null });
    setRecommendedSize(null);
    setRecommendedWidth(null);
  };


  // Staff mode functions
  const handleStaffLogin = () => {
    if (staffPassword.trim() === "Orza2026!") {
      console.log("Login successful");
      setStaffLoggedIn(true);
      sessionStorage.setItem("staffLoggedIn", "true");
    } else {
      console.log("Attempted password:", staffPassword);
      alert("Incorrect password. Please try: Orza2026!");
    }
  };

  const checkStaffSession = () => {
    const loggedIn = sessionStorage.getItem("staffLoggedIn");
    if (loggedIn === "true") {
      setStaffLoggedIn(true);
    }
  };

  const submitFeedback = async () => {
    const feedbackData = {
      gender,
      footLength: measurements.footLength,
      footWidth: measurements.footWidth,
      recommendedSize,
      recommendedWidth,
      sizeAccurate,
      actualFitSize: sizeAccurate ? recommendedSize : actualFitSize
    };
    
    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(feedbackData)
      });
      if (response.ok) {
        setFeedbackSubmitted(true);
      } else {
        alert("Error saving feedback. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error saving feedback. Please try again.");
    }
  };

  const fetchFeedbackData = async () => {
    try {
      const response = await fetch("/api/feedback", {
        headers: { "x-staff-password": "Orza2026!" }
      });
      const result = await response.json();
      if (result.success) {
        setFeedbackData(result.data);
      }
    } catch (error) {
      console.error("Error fetching feedback:", error);
    }
  };

  const fetchFeedbackStats = async () => {
    try {
      const response = await fetch("/api/feedback/stats", {
        headers: { "x-staff-password": "Orza2026!" }
      });
      const result = await response.json();
      if (result.success) {
        setFeedbackStats(result.stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };
  const generateSizeOptions = () => {
    const sizes = gender === "Women" ? 
      ["4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "10.5", "11"] :
      ["5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12", "12.5", "13", "13.5", "14"];
    const widths = ["N", "M", "W"];
    const options = [];
    sizes.forEach(size => {
      widths.forEach(width => {
        options.push(size + width);
      });
    });
    return options;
  };
  const widthDescriptions = {
    narrow: 'Narrow (N) - For slim feet',
    mid: 'Medium (M) - Standard width',
    wide: 'Wide (W) - For wider feet'
  };

  const getPlacementInstruction = () => {
    if (!referenceObject) return '';
    const ref = referenceObjects[referenceObject];
    if (ref.footOn) {
      return 'Place your foot ON the paper with your heel at the bottom edge';
    } else {
      return `Place your foot NEXT TO the ${ref.name}`;
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>ORZA Shoe Fit Finder</h1>
        <p className="subtitle">Find your perfect fit in minutes</p>
      </header>

      <div className="container">
        {step === 'welcome' && (
          <div className="step">
            <h2>Welcome!</h2>
            <p>Let's find your perfect ballet shoe size using a photo of your foot.</p>
            
            <div className="gender-selection">
              <h3>Select your sizing:</h3>
              <div className="gender-buttons">
                <button className={gender === 'Women' ? 'gender-btn active' : 'gender-btn'} onClick={() => setGender('Women')}>Women's</button>
                <button className={gender === 'Men' ? 'gender-btn active' : 'gender-btn'} onClick={() => setGender('Men')}>Men's</button>
              </div>
            </div>

            <div className="reference-selection">
              <h3>Select a reference object:</h3>
              <div className="reference-buttons">
                <button className={referenceObject === 'paper' ? 'ref-btn active' : 'ref-btn'} onClick={() => setReferenceObject('paper')}>
                  📄 Letter Paper (8.5x11") - Recommended
                </button>
                <button className={referenceObject === 'dollar' ? 'ref-btn active' : 'ref-btn'} onClick={() => setReferenceObject('dollar')}>
                  💵 US Dollar Bill
                </button>
                <button className={referenceObject === 'quarter' ? 'ref-btn active' : 'ref-btn'} onClick={() => setReferenceObject('quarter')}>
                  🪙 US Quarter
                </button>
                <button className={referenceObject === 'passport' ? 'ref-btn active' : 'ref-btn'} onClick={() => setReferenceObject('passport')}>
                  📘 Passport
                </button>
                <button className={referenceObject === 'card' ? 'ref-btn active' : 'ref-btn'} onClick={() => setReferenceObject('card')}>
                  💳 Credit/Debit Card
                </button>
              </div>
            </div>

            {referenceObject && (
              <div className="next-steps">
                <div className="instructions-box">
                  <p><strong>Instructions:</strong></p>
                  <ol>
                    <li>{getPlacementInstruction()}</li>
                    <li>Take a photo from directly above</li>
                    <li>Tap {getTapCount()} points on the photo to measure</li>
                  </ol>
                </div>
                <div className="button-group">
                  <button className="btn-primary" onClick={() => setStep('camera')}>TAKE PHOTO</button>
                  <button className="btn-secondary" onClick={() => setStep('upload')}>UPLOAD PHOTO</button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'camera' && (
          <div className="step">
            <h2>Take a Photo</h2>
            <p>{getPlacementInstruction()}</p>
            <div className="camera-container">
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }}
                onUserMediaError={() => { alert('Camera access denied. Please use Upload Photo instead.'); setStep('upload'); }}
              />
              {referenceObject === "paper" && <div className="paper-overlay"></div>}
            </div>
            <div className="button-group">
              <button className="btn-primary" onClick={capturePhoto}>CAPTURE PHOTO</button>
              <button className="btn-secondary" onClick={() => setStep('welcome')}>← BACK</button>
            </div>
          </div>
        )}

        {step === 'upload' && (
          <div className="step">
            <h2>Upload a Photo</h2>
            <p>{getPlacementInstruction()}</p>
            <div className="upload-container">
              <input type="file" accept="image/*" onChange={handleFileUpload} className="file-input" id="file-upload" />
              <label htmlFor="file-upload" className="file-label">CHOOSE PHOTO</label>
            </div>
            <button className="btn-secondary" onClick={() => setStep('welcome')}>← BACK</button>
          </div>
        )}

        {step === 'measure' && (
          <div className="step measure-step">
            <div className="instruction-banner">
              <p>{getCurrentInstruction()}</p>
              <div className="progress-dots">
                {Array.from({ length: getTapCount() }, (_, i) => (
                  <span key={i} className={`dot ${i < currentTapStep ? "completed" : ""} ${i === currentTapStep ? "current" : ""}`} />
                ))}
              </div>
            </div>
            
            <div className="image-container"
              onMouseMove={handleDrag}
              onTouchMove={handleDrag}
            >
              <img
                ref={imageRef}
                src={capturedImage}
                alt="Your foot"
                className="measure-image"
                onClick={handleImageTap}
                onTouchStart={handleImageTap}
              />
              {tapPoints.map((point, index) => (
                <div
                  key={index}
                  className="crosshair confirmed"
                  style={{ left: point.x, top: point.y }}
                >
                  <div className="crosshair-h"></div>
                  <div className="crosshair-v"></div>
                  <span className="crosshair-label">{index + 1}</span>
                </div>
              ))}
              {activePoint && (
                <div
                  className="crosshair active"
                  style={{ left: activePoint.x, top: activePoint.y }}
                >
                  <div className="crosshair-h"></div>
                  <div className="crosshair-v"></div>
                  <span className="crosshair-label">{currentTapStep + 1}</span>
                </div>
              )}
            </div>

            {activePoint && (
              <button className="btn-primary confirm-btn" onClick={confirmPoint}>
                CONFIRM POINT {currentTapStep + 1}
              </button>
            )}

            <div className="button-group">
              {tapPoints.length > 0 && (
                <button className="btn-secondary" onClick={undoLastTap}>UNDO LAST TAP</button>
              )}
              <button className="btn-secondary" onClick={resetMeasurement}>RESET ALL POINTS</button>
              <button className="btn-secondary" onClick={reset}>← START OVER</button>
            </div>
          </div>
        )}

        {step === "result" && (
          <div className="step result">
            <h2>Your Perfect Fit!</h2>
            
            <div className="measurements-display">
              <p>Your Measurements:</p>
              <div className="measurement-item">
                <span>Foot Length:</span>
                <strong>{measurements.footLength} cm</strong>
              </div>
              <div className="measurement-item">
                <span>Foot Width:</span>
                <strong>{measurements.footWidth} cm</strong>
              </div>
            </div>

            <div className="size-result">
              <p>Recommended Size:</p>
              <div className="size-display">{recommendedSize}</div>
              <p className="width-description">{widthDescriptions[recommendedWidth]}</p>
            </div>

            <div className="size-explanation">
              <p><strong>What this means:</strong></p>
              <ul>
                <li>US Size: {recommendedSize?.replace(/[NMW]$/, "")}</li>
                <li>Width: {recommendedWidth === "narrow" ? "Narrow (N)" : recommendedWidth === "mid" ? "Medium (M)" : "Wide (W)"}</li>
                <li>Gender: {gender}s sizing</li>
              </ul>
            </div>

            {/* Staff Feedback Section */}
            {isStaffMode && !staffLoggedIn && (
              <div className="staff-login">
                <h3>Staff Login</h3>
                <input
                  type="password"
                  placeholder="Enter staff password"
                  value={staffPassword}
                  onChange={(e) => setStaffPassword(e.target.value)}
                  className="staff-input"
                />
                <button className="btn-primary" onClick={handleStaffLogin}>LOGIN</button>
              </div>
            )}

            {isStaffMode && staffLoggedIn && !feedbackSubmitted && (
              <div className="staff-feedback">
                <h3>Staff Feedback</h3>
                <p>Is this size accurate?</p>
                <div className="feedback-buttons">
                  <button
                    className={sizeAccurate === true ? "btn-primary" : "btn-secondary"}
                    onClick={() => setSizeAccurate(true)}
                  >YES</button>
                  <button
                    className={sizeAccurate === false ? "btn-primary" : "btn-secondary"}
                    onClick={() => setSizeAccurate(false)}
                  >NO</button>
                </div>

                {sizeAccurate === false && (
                  <div className="actual-size-input">
                    <p>What size actually fit?</p>
                    <select
                      value={actualFitSize}
                      onChange={(e) => setActualFitSize(e.target.value)}
                      className="size-select"
                    >
                      <option value="">Select size...</option>
                      {generateSizeOptions().map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                )}

                {(sizeAccurate === true || (sizeAccurate === false && actualFitSize)) && (
                  <button className="btn-primary submit-feedback" onClick={submitFeedback}>
                    SUBMIT FEEDBACK
                  </button>
                )}
              </div>
            )}

            {isStaffMode && feedbackSubmitted && (
              <div className="feedback-success">
                <p>Thank you! Feedback recorded.</p>
              </div>
            )}

            {isStaffMode && staffLoggedIn && (
              <div className="view-feedback-section">
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    if (!showFeedbackList) {
                      fetchFeedbackData();
                      fetchFeedbackStats();
                    }
                    setShowFeedbackList(!showFeedbackList);
                  }}
                >
                  {showFeedbackList ? "HIDE FEEDBACK DATA" : "VIEW ALL FEEDBACK"}
                </button>

                {showFeedbackList && feedbackStats && (
                  <div className="feedback-stats">
                    <h4>Accuracy Stats</h4>
                    <p>Total Fittings: {feedbackStats.total}</p>
                    <p>Accurate: {feedbackStats.accurate}</p>
                    <p>Inaccurate: {feedbackStats.inaccurate}</p>
                    <p>Accuracy Rate: {feedbackStats.accuracyRate}</p>
                  </div>
                )}

                {showFeedbackList && feedbackData.length > 0 && (
                  <div className="feedback-list">
                    <h4>All Feedback ({feedbackData.length})</h4>
                    {feedbackData.slice().reverse().map((item, index) => (
                      <div key={item.id || index} className="feedback-item">
                        <p><strong>{item.gender} - {item.recommendedSize}</strong></p>
                        <p>Foot: {item.footLength}cm x {item.footWidth}cm</p>
                        <p>Accurate: {item.sizeAccurate ? "Yes" : "No"}</p>
                        {!item.sizeAccurate && <p>Actual Fit: {item.actualFitSize}</p>}
                        <p className="timestamp">{new Date(item.timestamp).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="button-group">
              {!isStaffMode && (
                <button className="btn-primary" onClick={returnToShopify}>SHOP SIZE {recommendedSize}</button>
              )}
              <button className="btn-secondary" onClick={reset}>TRY AGAIN</button>
            </div>
          </div>
        )}

      </div>
      <footer className="footer">
        <p>Powered by ORZA Brand | <a href="https://orzabrand.com">orzabrand.com</a></p>
      </footer>
    </div>
  );
}

export default App;